import { Edit } from '@mui/icons-material';
import { Box, IconButton, Skeleton, Stack, type SxProps, type Theme, Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { enqueueSnackbar } from 'notistack';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import useSWRMutation from 'swr/mutation';
import BayerModal from '/@/components/bayer/modal';
import { useAnchorEl } from '/@/hooks/anchor-el/useAnchorEl';
import { useRecommendationById } from '/@/hooks/my-recommendations/useFilterMyRecommendations';
import { _ } from '/@/locale';
import { getApplicationOptions, getCropOptions } from '/@/store/crop-protection/selectors';
import { updateRecommendationFromAPI } from '/@/store/private-data/recommendations/apiProviders';
import type { Recommendation } from '/@/types/recommendation';
import { capitalizeFirstLetter, toLocaleDateString } from '/@/utils';
import { reverse } from '/@/utils/router';

type RecommendationInputsCardProps =
    | {
          sx?: SxProps<Theme>;
          recommendation: Recommendation;
          recommendationId?: undefined;
          isPDF: true;
          pdfTheme?: Theme;
      }
    | {
          sx?: SxProps<Theme>;
          pdfTheme?: undefined;
          recommendation?: undefined;
          recommendationId: number;
          isPDF: false;
      };

const RecommendationInputsCard: React.FC<RecommendationInputsCardProps> = ({
    sx,
    isPDF = false,
    recommendation,
    pdfTheme,
    recommendationId,
}) => {
    const recommendationQuery = useRecommendationById(recommendationId || 0);
    const dataSource = isPDF ? recommendation : recommendationQuery?.data;
    const applicationOptions = useMemo(() => getApplicationOptions(), []);
    const cropOptions = useMemo(() => getCropOptions(), []);
    const [{ open }, { handleClick, handleClose }] = useAnchorEl();
    const history = useNavigate();
    const recommendationType = dataSource?.weedV2
        ? 'weeds'
        : dataSource?.diseaseRecommendation
          ? 'diseases'
          : dataSource?.seedTreatmentRecommendation
            ? 'seed_treatment'
            : dataSource?.plagueRecommendation
              ? 'plagues'
              : 'weeds';

    const renderInfoItem = (label: string, value: string, loading = false) => (
        <Stack>
            <Typography
                color={pdfTheme ? pdfTheme.mui.primary.colors.base : 'primary'}
                variant='caption'
                fontWeight='bold'
            >
                {label}
            </Typography>
            {loading ? (
                <Skeleton variant='text' sx={{ fontSize: (t) => t.typography.body2.fontSize }} />
            ) : (
                <Typography
                    color={pdfTheme ? pdfTheme.mui.primary.colors.inactive : 'primary.mui.inactive'}
                    variant='body2'
                    sx={{
                        wordBreak: 'break-all',
                    }}
                >
                    {value}
                </Typography>
            )}
        </Stack>
    );

    const recoData = useMemo(() => {
        return {
            title: {
                weeds: _('Maleza'),
                diseases: _('Enfermedad'),
                plagues: _('Plaga'),
                seed_treatment: _('Tratamiento de semilla'),
            },
            typeName: {
                weeds: _(dataSource?.weedV2?.result?.weed?.name) || _(dataSource?.weedV2?.result?.weedType?.name),
                diseases: _(dataSource?.diseaseRecommendation?.result?.disease?.name),
                plagues: _(dataSource?.plagueRecommendation?.result?.plague?.name),
                seed_treatment: '-',
            },
            scientificName: {
                weeds: dataSource?.weedV2?.result?.weed?.scientificName,
                diseases: dataSource?.diseaseRecommendation?.result?.disease?.scientificName,
                plagues: dataSource?.plagueRecommendation?.result?.plague?.scientificName,
                seed_treatment: undefined,
            },
            plantingDate: {
                weeds: dataSource?.weedV2?.plantingDate
                    ? toLocaleDateString(new Date(dataSource?.weedV2?.plantingDate), {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                      })
                    : '-',
                diseases: dataSource?.diseaseRecommendation?.plantingDate
                    ? toLocaleDateString(new Date(dataSource?.diseaseRecommendation?.plantingDate), {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                      })
                    : '-',
                plagues: dataSource?.plagueRecommendation?.plantingDate
                    ? toLocaleDateString(new Date(dataSource?.plagueRecommendation?.plantingDate), {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                      })
                    : '-',
                seed_treatment: dataSource?.seedTreatmentRecommendation?.plantingDate
                    ? toLocaleDateString(new Date(dataSource?.seedTreatmentRecommendation?.plantingDate), {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                      })
                    : '-',
            },
            crop: {
                weeds: _(cropOptions[dataSource?.weedV2?.result?.crop]) || '-',
                diseases: _(cropOptions[dataSource?.diseaseRecommendation?.result?.crop]) || '-',
                plagues: _(cropOptions[dataSource?.plagueRecommendation?.result?.crop]) || '-',
                seed_treatment: _(cropOptions[dataSource?.seedTreatmentRecommendation?.result?.crop]) || '-',
            },
            applicationMoment: {
                weeds:
                    _(
                        applicationOptions[dataSource?.weedV2?.result?.applicationMoment]?.title({
                            crop: dataSource?.weedV2?.result?.crop,
                        })
                    ) || '-',
                diseases:
                    _(applicationOptions[dataSource?.diseaseRecommendation?.result?.applicationMoment]?.title()) || '-',
                plagues:
                    _(applicationOptions[dataSource?.plagueRecommendation?.result?.applicationMoment]?.title()) || '-',
                seed_treatment: _('Siembra'),
            },
            applicationType: {
                weeds: _(applicationOptions[dataSource?.weedV2?.result?.applicationType]) || '-',
                diseases: _(applicationOptions[dataSource?.diseaseRecommendation?.result?.applicationType]) || '-',
                plagues: _(applicationOptions[dataSource?.plagueRecommendation?.result?.applicationType]) || '-',
            },
        };
    });

    const updateReco = useSWRMutation(
        {
            key: 'update_recommendation_mutation',
            id: recommendationQuery.data?.id,
            status: 'saved',
            weed_v2: recommendationQuery.data?.weedV2 || {},
            disease: recommendationQuery.data?.diseaseRecommendation || {},
            plague: recommendationQuery.data?.plagueRecommendation || {},
            field: recommendationQuery.data?.field,
            appInstance: recommendationQuery.data?.appInstance,
        },
        async (variables) => {
            try {
                await updateRecommendationFromAPI(variables);
                enqueueSnackbar(
                    _('¡Recomendación guardada con éxito! Podés consultarla en la sección “Mis recomendaciones”.'),
                    { variant: 'success' }
                );
                return history(
                    reverse('cropProtectionRecommendation', {
                        qs: {
                            fieldId: recommendationQuery?.data.field,
                            userNodeId: recommendationQuery?.data.userNode,
                        },
                    })
                );
            } catch (error) {
                enqueueSnackbar(_('No se pudo guardar la recomendación. Por favor, intenta nuevamente.'), {
                    variant: 'error',
                });
            }
        }
    );

    return (
        <Box sx={{ background: '#F5F5F5', ...sx }}>
            {!isPDF && (
                <BayerModal
                    variant='children'
                    open={open}
                    setClose={handleClose}
                    handleClick={() => {
                        return history(
                            reverse('cropProtectionRecommendation', {
                                params: {
                                    recommendationId: recommendationId,
                                },
                                qs: {
                                    fieldId: recommendationQuery?.data.field,
                                    userNodeId: recommendationQuery?.data.userNode,
                                },
                            })
                        );
                    }}
                    title={_('¿Deseas editar la recomendación?')}
                    buttonText={_('Continuar')}
                    secondaryButtonText={_('Cancelar')}
                    handleClickSecondary={() => handleClose()}
                >
                    <Typography
                        sx={{ p: 3 }}
                        color={pdfTheme ? pdfTheme.mui.primary.colors.base : 'primary'}
                        align={'center'}
                    >
                        {_(
                            'Si editas ahora, se perderán los datos actuales. Te sugerimos crear otra nueva recomendación.'
                        )}
                    </Typography>
                </BayerModal>
            )}
            <Stack spacing={2}>
                {recommendationQuery.isValidating ? (
                    <Stack>
                        <Skeleton variant='text' sx={{ fontSize: (t) => t.typography.subtitle1.fontSize }} />
                        <Skeleton variant='text' sx={{ fontSize: (t) => t.typography.caption.fontSize }} />
                    </Stack>
                ) : (
                    <Stack direction='row' alignItems='center' spacing={1} justifyContent='space-between'>
                        <Typography color={pdfTheme ? pdfTheme.mui.primary.colors.base : 'primary'} variant='h6'>
                            {recoData.title[recommendationType]}
                        </Typography>
                        {!isPDF && (
                            <IconButton size='large' color='secondary' onClick={handleClick}>
                                <Edit />
                            </IconButton>
                        )}
                    </Stack>
                )}
                {recommendationQuery.isValidating ? (
                    <Stack>
                        <Skeleton variant='text' sx={{ fontSize: (t) => t.typography.subtitle1.fontSize }} />
                        <Skeleton variant='text' sx={{ fontSize: (t) => t.typography.caption.fontSize }} />
                    </Stack>
                ) : (
                    <Stack>
                        <Typography color={pdfTheme ? pdfTheme.mui.primary.colors.base : 'primary'} variant='subtitle1'>
                            {capitalizeFirstLetter(recoData.typeName[recommendationType])}
                        </Typography>
                        <Typography
                            color={pdfTheme ? pdfTheme.mui.primary.colors.inactive : 'primary.mui.inactive'}
                            variant='caption'
                        >
                            {recoData.scientificName[recommendationType]}
                        </Typography>
                    </Stack>
                )}

                <Grid2 container rowSpacing={2}>
                    <Grid2 xs={12} pt={'0 !important'}>
                        {renderInfoItem(_('Lote'), dataSource.fieldName || '-', recommendationQuery.isValidating)}
                    </Grid2>

                    <Grid2 xs={12} sm={6}>
                        {renderInfoItem(_('Fecha de siembra'), recoData.plantingDate[recommendationType])}
                    </Grid2>

                    <Grid2 xs={12} sm={6}>
                        {renderInfoItem(_('Cultivo'), recoData.crop[recommendationType])}
                    </Grid2>
                    {dataSource?.weedV2 ||
                    dataSource?.plagueRecommendation ||
                    dataSource?.seedTreatmentRecommendation ? (
                        <Grid2 xs={12}>
                            {renderInfoItem(_('Momento de aplicación'), recoData.applicationMoment[recommendationType])}
                        </Grid2>
                    ) : dataSource?.diseaseRecommendation ? (
                        <Grid2 xs={12}>
                            {renderInfoItem(_('Tipo de aplicación'), recoData.applicationType[recommendationType])}
                        </Grid2>
                    ) : (
                        ''
                    )}

                    {dataSource?.weedV2?.result?.weedSize?.name && (
                        <Grid2 xs={12}>
                            {renderInfoItem(
                                _('Tamaño de la maleza'),
                                _(dataSource?.weedV2?.result?.weedSize?.name) || '-',
                                recommendationQuery.isValidating
                            )}
                        </Grid2>
                    )}
                </Grid2>
            </Stack>
        </Box>
    );
};

export default RecommendationInputsCard;
